<template>
  <v-card class="elevation-12">
    <v-toolbar color="primary" dark>
      {{ $t('usersTable.title') }}
    </v-toolbar>
    <v-data-table
        :headers="headers"
        :items="items"
        :server-items-length="total"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100, 200] }"
        :loading="loading"
        class="elevation-12"
    >
      <template v-slot:top>
        <v-sheet color="grey lighten-3"
                 class="px-4 pt-4 pb-3"
                 tile>
          <h3 class="transition-swing font-weight-medium">{{ $t('globalUsersTable.filterTitle') }}</h3>
          <v-responsive>
            <v-row>
              <v-col cols="12" sm="6" md="3" lg="3" style="padding-top: 16px;">
                <v-autocomplete v-model="filters.inn"
                                :items="organizations"
                                :label="$t('common.labels.organization')"
                                :dense="true"
                                :clearable="true"
                                item-text="name"
                                item-value="inn"
                                @change="onFiltersChanged"
                                style="padding: 2px"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="3" class="">
                <v-text-field
                    :label="$t('common.labels.identifier')"
                    v-model="filters.user"
                    :clearable="true"
                    @change="onFiltersChanged"
                    class="pt-0"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="3" class="">
                <v-text-field
                    :label="$t('common.labels.login')"
                    v-model="filters.username"
                    :clearable="true"
                    @change="onFiltersChanged"
                    class="pt-0"
                />
              </v-col>
              <v-spacer/>
              <!-- rga -->
              <v-col cols="12" sm="12" md="12" class="d-flex justify-space-between">
                <v-btn color="primary" @click="updateItems">
                  {{ $t('common.actions.update') }}
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
                <v-btn color="primary" @click="onAddUserButtonClicked">{{ $t('globalUsersTable.addUserBtn') }}</v-btn>
                <v-dialog v-model="addUserDialog" :persistent="true" width="600px">
                  <AddUserComponent
                      :inn="filters.inn"
                      @cancel="onAddUserCanceled"
                      @added="onAddUserSuccess"
                      ref="addAdminUserComponent"/>
                </v-dialog>
              </v-col>
            </v-row>
          </v-responsive>
        </v-sheet>
      </template>

      <template v-slot:item.organization.createdOn="{item}">
        {{ item.organization.createdOn.format('DD.MM.YYYY HH:mm:ss') }}
      </template>

      <template v-slot:item.name="{item}">
        <template v-if="item.isAdmin">
          <v-badge
              color="error"
              :content="$t('userTypes.admin')"
          >
            {{ item.name }}
          </v-badge>
        </template>
        <template v-else-if="item.isModerator">
          <v-badge
              color="success"
              :content="$t('userTypes.controller')"
          >
            {{ item.name }}
          </v-badge>
        </template>
        <template v-else-if="item.isMonitoring">
          <v-badge
              color="info"
              :content="$t('userTypes.monitoring')"
          >
            {{ item.name }}
          </v-badge>
        </template>
        <template v-else>
          {{ item.name }}
        </template>
      </template>

      <template v-slot:item.actions="{item}">
        <v-btn icon @click="onEditUserButtonClicked(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="editUserDialog" :persistent="true" width="600px">
      <EditUserComponent ref="editUser" @cancel="onEditUserCanceled" @updated="onEditUserSuccess" />
    </v-dialog>

    <v-snackbar v-model="isAddUserSuccess"
                :timeout="5000"
                :top="true"
                color="success"
    >
      {{ $t('globalUsersTable.messages.addSuccess') }}
    </v-snackbar>

    <v-snackbar v-model="isEditUserSuccess"
                :timeout="5000"
                :top="true"
                color="success"
    >
      {{ $t('globalUsersTable.messages.editSuccess') }}
    </v-snackbar>
  </v-card>
</template>


<script>
import _ from "underscore";
import AddUserComponent from "./AddUserComponent";
import EditUserComponent from "./EditUserComponent";
import {LAYOUT_LOADING_START, LAYOUT_LOADING_STOP} from "@/store/modules/layout/actions";
import OrganizationService from "../../services/organization.service";
import Mappers from "../../mappers";

export default {
  name: "UsersDataTable",
  props: {
    users: {
      type: Array,
      default: () => {
        return []
      }
    },
    total: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AddUserComponent,
    EditUserComponent,
  },
  data() {
    return {
      organizations: [],

      options: {
        page: 1,
        itemsPerPage: 50,
      },
      filters: {
        inn: null,
        user: null,
        username: null
      },

      addUserDialog: false,
      editUserDialog: false,
      isAddUserSuccess: false,
      isEditUserSuccess: false
    }
  },
  computed: {
    headers() {
      const headers = []

      headers.push({
        text: this.$t('globalUsersTable.headers.number'),
        align: 'center',
        sortable: false,
        value: 'displayNumber'
      })

      headers.push({
        text: this.$t('common.labels.fullName'),
        align: 'left',
        sortable: true,
        value: 'name'
      })

      headers.push({
        text: this.$t('common.labels.login'),
        align: 'left',
        sortable: true,
        value: 'username'
      })

      headers.push({
        text: this.$t('common.labels.organization'),
        align: 'left',
        sortable: true,
        value: 'organization.name'
      })

      headers.push({
        text: this.$t('globalUsersTable.headers.tin'),
        align: 'center',
        sortable: true,
        value: 'organization.inn'
      })

      headers.push({
        text: this.$t('globalUsersTable.headers.address'),
        align: 'left',
        sortable: true,
        value: 'organization.address'
      })

      headers.push({
        text: this.$t('common.labels.department'),
        align: 'center',
        sortable: true,
        value: 'unit.name'
      })

      headers.push({
        text: this.$t('globalUsersTable.headers.orgRegistrationDate'),
        align: 'center',
        sortable: true,
        value: 'organization.createdOn'
      })

      headers.push({
        text: '',
        align: 'right',
        sortable: false,
        value: 'actions'
      })

      return headers
    },
    items() {
      let number = 1;
      let skip = this.skip;
      return _.map(this.users, u => {
        u.displayNumber = skip + (number++)
        return u
      })

    },
    skip() {
      return (this.options.page - 1) * this.options.itemsPerPage
    }
  },
  methods: {
    updateItems() {
      this.$emit("update", this.options, this.filters)
    },

    onFiltersChanged() {
      if (this.options.page !== 1)
        this.options.page = 1;
      else
        this.updateItems();
    },
    onAddUserButtonClicked() {
      this.addUserDialog = true
      this.$nextTick(() => {
        this.$refs.addAdminUserComponent.init()
      })
    },
    onAddUserCanceled() {
      this.addUserDialog = false
    },
    onAddUserSuccess() {
      this.isAddUserSuccess = true
      this.addUserDialog = false
      this.updateItems()
    },
    onEditUserButtonClicked(user) {
      this.editUserDialog = true

      this.$nextTick(() => {
        this.$refs.editUser.set(user)
      })
    },
    onEditUserCanceled() {
      this.editUserDialog = false
    },
    onEditUserSuccess() {
      this.isEditUserSuccess = true
      this.editUserDialog = false
      this.updateItems()
    },
    loadOrganizations() {
      this.$store.dispatch(LAYOUT_LOADING_START)

      OrganizationService.getOrganizations({})
          .then(response => {
            this.organizations = _.map(response.data.Records, o => {
              return Mappers.mapOrganization(o?.Organization)
            })
          })
          .finally(() => {
            this.$store.dispatch(LAYOUT_LOADING_STOP)
          })
    },

    loadUsernames() {
      this.$store.dispatch(LAYOUT_LOADING_START)
    }
  },
  created() {
    this.loadOrganizations()

    if (this.$route.query.inn)
      this.filters.inn = this.$route.query.inn

    // this.updateItems()
  },
  watch: {
    options: {
      handler() {
        this.updateItems()
      }
    }
  }
}
</script>

<style scoped>

</style>